/** Graph input is ordinary authorized View rows and saved existing-relation mappings. */
export interface HierarchyNodeMapping {
  data_source_alias: string;
  table_id: string;
  id_field: string;
  label_field: string;
  subtitle_field?: string | null;
  avatar_field?: string | null;
  icon_field?: string | null;
  status_field?: string | null;
  status_colors?: Record<string, string>;
  node_click_action?: {
    type?: string;
    config?: Record<string, unknown>;
  } | null;
}
export interface HierarchyRelationMapping {
  relationship_ids: string[];
  from_data_source_alias: string;
  to_data_source_alias: string;
  from_field: string;
  to_field: string;
  link_data_source_alias?: string | null;
  type_field?: string | null;
  label_field?: string | null;
  reverse?: boolean;
  relationship_name?: string | null;
}
export interface HierarchyConfig {
  nodes: HierarchyNodeMapping[];
  relationships: HierarchyRelationMapping[];
  layout: "tree" | "list" | "flow" | "radial";
  initial_depth?: number;
  max_depth?: number;
  starting_items?: Record<string, (string | number)[]>;
  relationship_styles?: Record<string, { color: string; dashed?: boolean }>;
}
export interface HierarchyNode {
  key: string;
  alias: string;
  recordId: string | number;
  label: string;
  subtitle: string;
  avatar: string;
  icon: string;
  status: string;
  parentUnavailable: boolean;
}
export interface HierarchyEdge {
  key: string;
  from: string;
  to: string;
  kind: string;
  label: string;
}
export interface HierarchyGraph {
  nodes: HierarchyNode[];
  edges: HierarchyEdge[];
  roots: string[];
  missingIdentityCount: number;
}
const text = (value: unknown): string => (value == null ? "" : String(value));
const validId = (value: unknown): value is string | number =>
  (typeof value === "string" && value.trim().length > 0) ||
  (typeof value === "number" && Number.isSafeInteger(value));
const identity = (table: string, id: string | number) =>
  JSON.stringify([table, String(id)]);

export function buildHierarchyGraph(
  config: HierarchyConfig,
  rows: Record<string, Record<string, unknown>[]>,
): HierarchyGraph {
  const nodes = new Map<string, HierarchyNode>();
  const mappings = new Map(
    config.nodes.map((mapping) => [mapping.data_source_alias, mapping]),
  );
  let missingIdentityCount = 0;
  for (const mapping of config.nodes) {
    for (const row of rows[mapping.data_source_alias] || []) {
      const id = row[mapping.id_field];
      if (!validId(id)) {
        missingIdentityCount++;
        continue;
      }
      const key = identity(mapping.table_id, id);
      if (nodes.has(key)) continue;
      const navigationId = validId(row._rowId) ? row._rowId : id;
      nodes.set(key, {
        key,
        alias: mapping.data_source_alias,
        recordId: navigationId,
        label: text(row[mapping.label_field]),
        subtitle: mapping.subtitle_field
          ? text(row[mapping.subtitle_field])
          : "",
        avatar: mapping.avatar_field ? text(row[mapping.avatar_field]) : "",
        icon: mapping.icon_field ? text(row[mapping.icon_field]) : "",
        status: mapping.status_field ? text(row[mapping.status_field]) : "",
        parentUnavailable: false,
      });
    }
  }
  const edges = new Map<string, HierarchyEdge>();
  for (const relation of config.relationships) {
    const source = mappings.get(relation.from_data_source_alias);
    const target = mappings.get(relation.to_data_source_alias);
    if (!source || !target) continue;
    const link = relation.link_data_source_alias;
    for (const row of rows[link || source.data_source_alias] || []) {
      const fromId = link ? row[relation.from_field] : row[source.id_field];
      const toId = link ? row[relation.to_field] : row[relation.from_field];
      if (!validId(fromId) || !validId(toId)) continue;
      let from = identity(source.table_id, fromId);
      let to = identity(target.table_id, toId);
      const visibleSource = nodes.get(from);
      const visibleTarget = nodes.get(to);
      if (!visibleSource || !visibleTarget) {
        // Retain only a boolean: never copy a hidden row's foreign key into the graph.
        if (visibleSource && !visibleTarget && (!link || relation.reverse))
          visibleSource.parentUnavailable = true;
        if (link && !relation.reverse && visibleTarget && !visibleSource)
          visibleTarget.parentUnavailable = true;
        continue;
      }
      if (relation.reverse) [from, to] = [to, from];
      const kind = relation.type_field
        ? text(row[relation.type_field]) ||
          relation.relationship_name ||
          "Relationship"
        : relation.relationship_name || "Relationship";
      const label = relation.label_field ? text(row[relation.label_field]) : "";
      const key = JSON.stringify([
        relation.relationship_ids,
        from,
        to,
        kind,
        label,
      ]);
      if (!edges.has(key)) edges.set(key, { key, from, to, kind, label });
    }
  }
  const starting = Object.entries(config.starting_items || {}).flatMap(
    ([alias, values]) => {
      const mapping = mappings.get(alias);
      return mapping
        ? values
            .map((id) => identity(mapping.table_id, id))
            .filter((key) => nodes.has(key))
        : [];
    },
  );
  const hasStarting = Object.values(config.starting_items || {}).some(
    (values) => values.length > 0,
  );
  const roots = hasStarting
    ? [...new Set(starting)]
    : hierarchyRoots([...nodes.values()], [...edges.values()]);
  return {
    nodes: [...nodes.values()],
    edges: [...edges.values()],
    roots,
    missingIdentityCount,
  };
}

export interface HierarchyBranch {
  key: string;
  nodeKey: string;
  depth: number;
  cycle: boolean;
  shared: boolean;
  depthLimited: boolean;
}
/** Bounded, iterative traversal. Repeated identities are references, never duplicated records. */
export function visibleHierarchyBranches(
  graph: HierarchyGraph,
  depthLimit: number,
): HierarchyBranch[] {
  const limit = Math.min(50, Math.max(1, depthLimit));
  const children = new Map<string, string[]>();
  const incoming = new Map<string, Set<string>>();
  for (const edge of graph.edges) {
    children.set(edge.from, [
      ...new Set([...(children.get(edge.from) || []), edge.to]),
    ]);
    const parents = incoming.get(edge.to) || new Set<string>();
    parents.add(edge.from);
    incoming.set(edge.to, parents);
  }
  const pending = [...graph.roots].reverse().map((nodeKey) => ({
    nodeKey,
    depth: 1,
    path: new Set<string>(),
    branch: nodeKey,
  }));
  const expanded = new Set<string>();
  const result: HierarchyBranch[] = [];
  while (pending.length) {
    const current = pending.pop()!;
    const cycle = current.path.has(current.nodeKey);
    const repeated = expanded.has(current.nodeKey);
    const descendants = children.get(current.nodeKey) || [];
    const depthLimited =
      !cycle && descendants.length > 0 && current.depth >= limit;
    result.push({
      key: current.branch,
      nodeKey: current.nodeKey,
      depth: current.depth,
      cycle,
      shared: repeated || (incoming.get(current.nodeKey)?.size || 0) > 1,
      depthLimited,
    });
    if (cycle || repeated || depthLimited) continue;
    expanded.add(current.nodeKey);
    const path = new Set(current.path);
    path.add(current.nodeKey);
    [...descendants].reverse().forEach((nodeKey, index) =>
      pending.push({
        nodeKey,
        depth: current.depth + 1,
        path,
        branch: `${current.branch}/${index}:${nodeKey}`,
      }),
    );
  }
  return result;
}

export function hierarchyRoots(
  nodes: HierarchyNode[],
  edges: HierarchyEdge[],
): string[] {
  const incoming = new Set(edges.map((edge) => edge.to));
  const roots = nodes
    .map((node) => node.key)
    .filter((key) => !incoming.has(key));
  const children = new Map<string, string[]>();
  for (const edge of edges.values())
    children.set(edge.from, [...(children.get(edge.from) || []), edge.to]);
  const reached = new Set<string>();
  function markReachable(root: string) {
    const pending = [root];
    while (pending.length) {
      const current = pending.pop()!;
      if (reached.has(current)) continue;
      reached.add(current);
      pending.push(...(children.get(current) || []));
    }
  }
  roots.forEach(markReachable);
  // A component made entirely of cycles has no natural root and must remain accessible.
  for (const key of nodes.map((node) => node.key))
    if (!reached.has(key)) {
      roots.push(key);
      markReachable(key);
    }
  return roots;
}
